"use client";

import Link from "next/link";
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { DAY_OUT_PARAM, dayOutConditionsHref, dayOutLocal, dayOutParam, dayOutPlace, dayOutPlannerHref, dayOutRouteMatches, dayOutSportLabel, dayOutStatus, parseDayOut, withDayOut, type DayOut } from "@/lib/companion/day-out";
import { downloadSavedDayOut, readSavedDayOut, removeSavedDayOut, saveDayOut, savedDayOutSnapshot, subscribeSavedDayOut } from "@/lib/companion/saved-day-out";
import { readSavedRoute, savedRouteSnapshot, subscribeSavedRoute } from "@/lib/planner/saved-route";
import { setShallowSearchParams, useShallowSearchParam } from "@/lib/hooks/use-shallow-search-param";

const serverSnapshot = () => null;
const subscribeReady = () => () => {};
const clientReady = () => true;
const serverReady = () => false;
const linkClass = "inline-flex min-h-[44px] items-center justify-center rounded-sm border border-border bg-white px-3 py-2 text-sm font-semibold text-navy hover:border-teal";

export function useDayOut() {
  const raw = useShallowSearchParam(DAY_OUT_PARAM);
  const ready = useSyncExternalStore(subscribeReady, clientReady, serverReady);
  return useMemo(() => ({ raw, selection: parseDayOut(raw), ready }), [raw, ready]);
}

export function useDayOutClock() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const timer = window.setInterval(() => setNow(Date.now()), 30_000); return () => window.clearInterval(timer); }, []);
  return new Date(now);
}

export function SelectedDayOut({ planner = false }: { planner?: boolean }) {
  const { raw, selection } = useDayOut();
  const coverageUnavailable = useShallowSearchParam("planner-unavailable") === "1";
  const snapshot = useSyncExternalStore(subscribeSavedRoute, savedRouteSnapshot, serverSnapshot);
  const saved = useMemo(() => readSavedRoute(snapshot), [snapshot]);
  if (!raw) return null;
  if (!selection) return <section role="status" className="mb-5 rounded-md border border-border bg-white p-4 text-sm text-navy">
    <p>This day-out link is incomplete or no longer recognised. Choose a fresh window.</p>
    <button type="button" className={linkClass + " mt-3"} onClick={() => setShallowSearchParams({ [DAY_OUT_PARAM]: null })}>Clear this selection</button>
    <Link href="/conditions" className={linkClass + " ml-2 mt-3"}>Find conditions</Link>
  </section>;
  return <DayOutSummary selection={selection} planner={planner} coverageUnavailable={coverageUnavailable} missingRoute={Boolean(selection.journeyId && !dayOutRouteMatches(selection, saved))} onClear={() => setShallowSearchParams({ [DAY_OUT_PARAM]: null, "planner-unavailable": null })} />;
}

export function PublicDayOutSelection() {
  const { raw } = useDayOut();
  return raw ? <div className="mx-auto max-w-[1160px] px-5 pt-5 md:px-10"><SelectedDayOut /></div> : null;
}

export function DayOutSummary({ selection, planner = false, missingRoute = false, coverageUnavailable = false, onClear, saved = false }: {
  selection: DayOut; planner?: boolean; missingRoute?: boolean; coverageUnavailable?: boolean; onClear?: () => void; saved?: boolean;
}) {
  const now = useDayOutClock();
  const status = dayOutStatus(selection, now);
  const local = dayOutLocal(selection);
  const place = dayOutPlace(selection);
  const fresh = new URL(dayOutConditionsHref(selection), "https://peakwave.co");
  fresh.searchParams.delete(DAY_OUT_PARAM);
  const dateLabel = new Intl.DateTimeFormat("en-GB", { timeZone: selection.timezone, weekday: "short", day: "numeric", month: "short" }).format(new Date(selection.startAt));
  return <section aria-label="Selected day out" className="mb-5 rounded-md border border-teal/30 bg-white p-4 md:p-5">
    <p className="text-[11px] font-semibold uppercase tracking-[1.6px] text-teal">{saved ? "Saved on this device" : "Your selected day out"}</p>
    <h2 className="mt-1 font-heading text-xl font-bold text-navy">{place?.name} · {dayOutSportLabel(selection.sport)}</h2>
    <p className="mt-1 text-sm text-text-body">{dateLabel}, {local.startTime} to {local.endTime}{local.endDate !== local.date ? ` (${local.endDate})` : ""}</p>
    <p className="mt-1 text-xs text-text-muted">Local time in {selection.timezone.replaceAll("_", " ")}</p>
    {coverageUnavailable && <p role="status" className="mt-3 text-sm text-text-body">Mapped routes are unavailable for this place. Your selected window is retained; use the current conditions and local maps to plan.</p>}
    {missingRoute ? <p role="status" className="mt-3 text-sm text-terracotta">This saved route is missing or has changed on this device. Review a new route before arranging or starting it.</p>
      : status === "ended" ? <p role="status" className="mt-3 text-sm text-text-body">This window has passed. Record the day you had, or review fresh conditions for another plan.</p>
        : <p className="mt-3 text-sm text-text-muted">Review current conditions before setting off. Meet-ups and recording each start with your confirmation.</p>}
    <div className="mt-3 flex flex-wrap gap-2">
      {missingRoute || status === "ended" ? <Link href={`${fresh.pathname}${fresh.search}`} className={linkClass}>Review fresh conditions</Link>
        : <>
          {!planner && !coverageUnavailable && <Link href={dayOutPlannerHref(selection)} className={linkClass}>{selection.journeyId ? "Review saved route" : "Review the planner"}</Link>}
          <Link href={withDayOut("/companion/people", selection)} className={linkClass}>Arrange a meet-up</Link>
          <Link href={withDayOut("/companion/track", selection)} className={linkClass}>Set up recording</Link>
        </>}
      <Link href={withDayOut("/companion/record", selection)} className={linkClass}>Record how it went</Link>
      {onClear && <button type="button" onClick={onClear} className="min-h-[44px] px-2 text-sm font-semibold text-text-muted">{saved ? "Remove saved window" : "Clear selection"}</button>}
    </div>
  </section>;
}

export function SaveSessionWindow({ selection }: { selection: DayOut }) {
  const [message, setMessage] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const snapshot = useSyncExternalStore(subscribeSavedDayOut, savedDayOutSnapshot, serverSnapshot);
  const isSaved = dayOutParam(readSavedDayOut(snapshot)) === dayOutParam(selection);
  const now = useDayOutClock();
  return <div className="mt-4 border-t border-border-light pt-3">
    <div className="flex flex-wrap gap-2">
      <button type="button" className={linkClass + " !border-teal !bg-teal !text-white"} onClick={() => {
        if (dayOutStatus(selection, new Date()) === "ended") { setMessage("This window has passed. Review fresh conditions before saving another."); return; }
        if (!saveDayOut(selection)) { setMessage("This device could not save the window. You can still continue using the link below."); return; }
        setShallowSearchParams({ [DAY_OUT_PARAM]: dayOutParam(selection) });
        setMessage("Window saved on this device. Your latest saved window is available in Plan.");
      }} disabled={dayOutStatus(selection, now) === "ended"}>{isSaved ? "Window saved" : "Save this window"}</button>
      <Link href={withDayOut("/companion/plan/new", selection)} className={linkClass}>Save as an outing</Link>
      <Link href={withDayOut("/companion/plan", selection)} className={linkClass}>Continue in Companion</Link>
      <button type="button" className={linkClass} disabled={downloading} onClick={() => {
        setDownloading(true);
        void downloadSavedDayOut(selection).then(() => setMessage("Window downloaded to Offline home. It contains the chosen times, not a live forecast."))
          .catch(error => setMessage(error instanceof Error ? error.message : "The download failed. Retry when device storage is available."))
          .finally(() => setDownloading(false));
      }}>{downloading ? "Downloading…" : "Keep window offline"}</button>
      <Link href="/companion/offline" className={linkClass}>Offline home and downloads</Link>
    </div>
    <p aria-live="polite" className="mt-2 text-xs text-text-muted">{message ?? "Keep this time window on this device. No tracking or sharing starts."}</p>
  </div>;
}

export function SavedSessionWindowCard({ readOnly = false }: { readOnly?: boolean }) {
  const { raw } = useDayOut();
  const snapshot = useSyncExternalStore(subscribeSavedDayOut, savedDayOutSnapshot, serverSnapshot);
  const saved = useMemo(() => readSavedDayOut(snapshot), [snapshot]);
  const [message, setMessage] = useState<string | null>(null);
  if (!saved || raw) return null;
  return <><DayOutSummary selection={saved} saved onClear={readOnly ? undefined : () => { if (!removeSavedDayOut()) setMessage("The window could not be removed. Try again when device storage is available."); }} />{message && <p role="status">{message}</p>}</>;
}
